import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, Paper, Tabs, Tab, Typography } from '@mui/material';

// Restro Reports workspace — NAVIGATION ONLY.
//
// A horizontal tab strip over the four restaurant reports, so they are read
// as one set instead of four separate entries buried in the Reports menu.
// Every tab navigates to an EXISTING route; the report pages, their data and
// their permissions are untouched.
//
// Same shape as RestaurantOrdersLayout on purpose — one tab-strip pattern in
// the product, not two that drift apart.
const TABS = [
  {
    label: 'Invoices',
    path: '/admin/RestaurantInvoiceReport',
    menuKey: 'RestaurantInvoiceReport',
  },
  {
    label: 'Activity',
    path: '/admin/RestaurantActivity',
    menuKey: 'RestaurantActivity',
  },
  {
    label: 'Guests',
    path: '/admin/RestaurantGuestReport',
    menuKey: 'RestaurantGuestReport',
  },
  {
    label: 'Sale (Item / Category)',
    path: '/admin/RestaurantSaleReport',
    menuKey: 'RestaurantSaleReport',
  },
  // The end-of-day / end-of-shift closing statement. Shares the Invoices
  // permission: whoever may read the restaurant's invoices may read its
  // closing figures, and a key of its own would have to be granted to every
  // existing user again before the tab appeared for anybody.
  {
    label: 'Z Report',
    path: '/admin/ZReport',
    menuKey: 'RestaurantInvoiceReport',
  },
  // The owner's statement: sales, cost of goods, expenses, operating
  // profit for a period. Same key as Z Report, for the same reason.
  {
    label: 'Profit & Loss',
    path: '/admin/RestaurantPnL',
    menuKey: 'RestaurantInvoiceReport',
  },
  // Is anything stuck between the till and the printer? Its own key, because
  // it is a manager's screen, not a sales report - and because it is granted
  // to whoever already has KOT Report (see Add_PrinterHealth_MenuKey.sql).
  {
    label: 'Printer Health',
    path: '/admin/PrinterHealth',
    menuKey: 'PrinterHealth',
  },
];

// Same visibility rule as the sidebar: master admin sees all, otherwise the
// stored permission grants decide; fail-open when no permission list exists.
const allowedTabs = () => {
  let user = null;
  try {
    user = JSON.parse(sessionStorage.getItem('user'));
  } catch (_) {}
  if (user?.isMaster) return TABS;

  const raw = sessionStorage.getItem('permissions');
  if (!raw) return TABS;
  try {
    const keys = new Set(JSON.parse(raw).map((p) => p.menuKey));
    return TABS.filter((t) => keys.has(t.menuKey));
  } catch (_) {
    return TABS;
  }
};

// For the header search - the same filter the strip uses. See
// RestaurantMastersLayout.
export const allowedRestroReportTabs = () => allowedTabs();

export default function RestroReportsLayout() {
  const location = useLocation();
  const navigate = useNavigate();

  const tabs = React.useMemo(allowedTabs, []);
  const current = location.pathname.replace(/\/+$/, '');

  const activeIdx = tabs.findIndex(
    (t) => current === t.path || current.startsWith(t.path + '/'),
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        elevation={0}
        square
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: { xs: 1, sm: 2 },
          pt: 1,
          bgcolor: 'background.paper',
        }}>
        <Typography
          variant='subtitle2'
          sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>
          Restro Report
        </Typography>
        <Tabs
          value={activeIdx === -1 ? false : activeIdx}
          onChange={(_, idx) => navigate(tabs[idx].path)}
          variant='scrollable'
          scrollButtons='auto'
          allowScrollButtonsMobile
          sx={{
            minHeight: 40,
            '& .MuiTab-root': {
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 600,
            },
          }}>
          {tabs.map((t) => (
            <Tab key={t.label} label={t.label} />
          ))}
        </Tabs>
      </Paper>

      {/* The existing report renders below, completely unchanged. */}
      <Outlet />
    </Box>
  );
}
